import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Tag,
  Typography,
  Select,
  Divider,
  App,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { addDays, addHours, endOfDay, format, isBefore, set } from 'date-fns';
import { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { RRule, RRuleSet } from 'rrule';
import { useLoggedInUser } from '../../hooks/useLoggedInUser';
import useModifyShift from '../../hooks/useModifyShift';
import { usePermission } from '../../hooks/usePermission';
import { eventData } from '../../lib/graphql/common/queries';
import { userFullName } from '../../lib/utils';
import {
  shiftDateFormat,
  shiftDateTimeFormat,
  shiftTimeFormat,
} from '../../lib/utils/date-time';
import '../../styles/roster.css';
import rfsTheme from '../../theme';
import DatePicker from '../DatePicker';
import SearchUserSelect from '../SearchUserSelect';
import { Optional } from '../Shared';
import {
  maximumShiftPeriodInHours,
  minimumShiftPeriodInHours,
} from './lib/constants';
import {
  mapToRRuleWeekdayNumber,
  newRecurRule,
  newRecurRuleSet,
  parseRRuleSet,
  removeApproximateFromRecurrentPatternText,
  weekdays,
  mapRRuleSetFreq,
  FreqOptionMap,
  newCustomRecurRuleSet,
  mapCustomRecurDay,
  FreqTextMap,
} from './lib/RRule';
import {
  adjustRecurEndTime,
  performModifyShift,
  durationOrDefault,
  initialTime,
  removeSingleRecurringShiftModifiers,
  saveSingleShiftModifiers,
  shiftErrors,
  ShiftModifiers,
  validateShiftDuration,
  futureRecurrentShiftModifiers,
  removeFutureRecurrentShiftModifiers,
  rootId,
  createNoteToCreate,
  emptyShiftModifiers,
  ShiftExceptionUpdate,
} from './lib/shift-service';
import { addChangelogs, shiftDeletedNote } from './lib/changeLog';
import {
  ShiftCellRecord,
  ShiftPeriod,
  FilledShift,
  SimpleUser,
  shiftFreqOption,
  ShiftFreqOption,
} from './types';
import { determineInitialRecurEndDate } from './lib/util';
import {
  shiftStatusMap,
  ShiftStatusTag,
  vacantShiftStatusValues,
  filledShiftStatusValues,
  vacantShiftDefaultStatus,
  filledShiftDefaultStatus,
} from './lib/shift-status';
import { SimpleTable } from '../SimpleTable';
import PageLoadingSpinner from '../PageLoadingSpinner';
import { toNoteContent, validNotesOrUndefined } from '../Notes/util';
import ShiftDisplayHeading from './Shift/ShiftDisplayHeading';
import { InputDuration } from '../Teams/TeamEditor/InputDuration';
import { useNotify } from '../../hooks/useNotify';
import { onApolloError } from '../../lib/graphql/helpers';

const { Text } = Typography;

const { CheckableTag } = Tag;

const { TextArea } = Input;

const { Option } = Select;

const { TimePicker } = DatePicker;

type ShiftManagerProps = {
  readonly onFinish: () => void;
  readonly shift?: FilledShift;
  readonly record: ShiftCellRecord;
  readonly defaultHours: ShiftPeriod;
  readonly titleWidth: string;
};

// Common stylesheet used in the titles of the rows of this component's tables
const titleStyle: CSSProperties = {
  verticalAlign: 'sub',
};

/**
 * Renders a section to manage a new or given Shift (create or edit).
 */
const ShiftManager = ({
  shift: providedShift,
  record,
  onFinish,
  defaultHours,
  titleWidth,
}: ShiftManagerProps) => {
  const { modal } = App.useApp();
  const { notify } = useNotify();
  const { loggedInUser } = useLoggedInUser();

  const { teamId } = useParams<{ readonly teamId: string | undefined }>();

  const permission = usePermission(teamId, loggedInUser);

  const editShiftPermission = permission.teamAssignTeamRole;

  const isNewShift = providedShift?.id === undefined;

  const initialStartTime = initialTime(
    defaultHours.start,
    providedShift?.startTime === undefined
      ? undefined
      : new Date(providedShift.startTime),
    record
  );

  const initialEndTime = initialTime(
    defaultHours.end,
    providedShift?.endTime === undefined
      ? undefined
      : new Date(providedShift.endTime),
    record
  );

  const initialShiftDuration = durationOrDefault(
    defaultHours.end - defaultHours.start,
    initialStartTime,
    initialEndTime
  );

  const [selectedUsers, setSelectedUsers] = useState<readonly SimpleUser[]>(
    providedShift?.user !== undefined ? [providedShift.user] : []
  );

  const [startTime, setStartTime] = useState(initialStartTime);

  const [endTime, setEndTime] = useState(initialEndTime);

  const defaultRRuleOptions = {
    freq: RRule.DAILY,
    interval: 1,
    dtstart: startTime,
  };

  const initialShiftRecurRuleSet =
    providedShift?.recurrencePattern === undefined ||
    providedShift.recurrencePattern === ''
      ? newRecurRuleSet(defaultRRuleOptions)
      : parseRRuleSet(providedShift.recurrencePattern);

  const firstShiftRecurRule =
    initialShiftRecurRuleSet.rrules()[0] ?? newRecurRule(defaultRRuleOptions);

  const isCustomRule = initialShiftRecurRuleSet.rrules().length > 1;

  const [recurFreqOption, setRecurFreqOption] = useState(
    mapRRuleSetFreq(initialShiftRecurRuleSet)
  );

  const [shiftCustomRecurDay, setShiftCustomRecurDay] = useState<
    readonly number[]
  >(
    isCustomRule ? mapCustomRecurDay(initialShiftRecurRuleSet, startTime) : [0]
  );

  const [isRecurring, setIsRecurring] = useState(
    providedShift?.isRecurring ?? false
  );

  const [shiftRecurFreq, setShiftRecurFreq] = useState(
    firstShiftRecurRule.options.freq
  );

  const [shiftRecurWeekday, setShiftRecurWeekday] = useState<readonly number[]>(
    firstShiftRecurRule.options.freq === RRule.WEEKLY
      ? firstShiftRecurRule.options.byweekday
      : []
  );

  const [shiftRecurInterval, setShiftRecurInterval] = useState(
    firstShiftRecurRule.options.interval
  );

  const [shiftRecurEndDate, setShiftRecurEndDate] = useState<Date | null>(
    determineInitialRecurEndDate(firstShiftRecurRule, providedShift)
  );

  const [shiftRecurrenceRule, setShiftRecurrenceRule] = useState<
    RRuleSet | undefined
  >(undefined);

  const [shiftDurationInHours, setShiftDurationInHours] =
    useState(initialShiftDuration);

  const [shiftStatus, setShiftStatus] = useState(
    providedShift?.status ?? vacantShiftDefaultStatus
  );

  const [rawShiftNotes, setShiftNotes] = useState<string>('');

  const [modifyShift] = useModifyShift(onApolloError(notify.error));

  const userName = userFullName({
    first_name: record.firstName,
    last_name: record.lastName,
  });

  const isVacantShift =
    record.userId === undefined && selectedUsers.length === 0;

  // Make sure the start/end time are reset to new initial when record changes.
  useEffect(() => {
    const nextInitStartTime = initialTime(
      defaultHours.start,
      providedShift?.startTime === undefined
        ? undefined
        : new Date(providedShift.startTime),
      record
    );

    const nextInitEndTime = initialTime(
      defaultHours.end,
      providedShift?.endTime === undefined
        ? undefined
        : new Date(providedShift.endTime),
      record
    );

    setStartTime(nextInitStartTime);

    setEndTime(nextInitEndTime);
  }, [record, providedShift?.endTime, providedShift?.startTime, defaultHours]);

  useEffect(() => {
    if (isRecurring) {
      const nextShiftRecurRuleSet =
        recurFreqOption === shiftFreqOption.Values.CUSTOM
          ? newCustomRecurRuleSet(
              {
                freq: shiftRecurFreq,
                interval: shiftRecurInterval,
                dtstart: startTime,
                until: shiftRecurEndDate,
              },
              shiftCustomRecurDay
            )
          : newRecurRuleSet({
              freq: shiftRecurFreq,
              interval: shiftRecurInterval,
              dtstart: startTime,
              until: shiftRecurEndDate,
              byweekday: [...shiftRecurWeekday],
            });
      setShiftRecurrenceRule(nextShiftRecurRuleSet);
    }
  }, [
    shiftRecurFreq,
    shiftRecurWeekday,
    shiftRecurEndDate,
    shiftRecurInterval,
    isRecurring,
    startTime,
    recurFreqOption,
    shiftCustomRecurDay,
  ]);

  // Make sure current shift status is valid.
  useEffect(() => {
    if (isVacantShift && !vacantShiftStatusValues.includes(shiftStatus)) {
      setShiftStatus(vacantShiftDefaultStatus);
    } else if (
      !isVacantShift &&
      !filledShiftStatusValues.includes(shiftStatus)
    ) {
      setShiftStatus(filledShiftDefaultStatus);
    }
  }, [isVacantShift, shiftStatus]);

  const onStartTimeChange = useCallback(
    (changedDate: Date | null) => {
      if (changedDate !== null) {
        const updatedStartTime = set(changedDate, {
          year: initialStartTime?.getFullYear(),
          month: initialStartTime?.getMonth(),
          date: initialStartTime?.getDate(),
        });

        setStartTime(updatedStartTime);

        setEndTime(addHours(updatedStartTime, shiftDurationInHours));
      } else {
        setStartTime(initialStartTime);
      }
    },
    [shiftDurationInHours, initialStartTime]
  );

  if (loggedInUser === undefined) {
    return <PageLoadingSpinner />;
  }

  const shiftDurationInHoursIsValid =
    validateShiftDuration(shiftDurationInHours);

  // Either creates or updates a Shift.
  const onSaveSingleShift = async () => {
    const shiftEndTime = adjustRecurEndTime(isRecurring, startTime, endTime);

    const formErrors = shiftErrors(
      shiftDurationInHoursIsValid,
      startTime,
      shiftEndTime
    );

    if (formErrors.length > 0) {
      notify.error(`${formErrors.join(', ')}, please review the form.`);
      return;
    }

    // Button handler cannot be promise based and cannot be awaited for resolve.
    const shiftModifiers: ShiftModifiers = saveSingleShiftModifiers({
      currentShift: providedShift,
      isNewShift,
      selectedUsers,
      shiftUserRoleAssignmentId: record.userRoleAssignmentId,
      shiftProps: {
        rootId: providedShift === undefined ? undefined : rootId(providedShift),
        id: providedShift?.id,
        startTime: startTime,
        endTime: shiftEndTime,
        status: shiftStatus,
        note: validNotesOrUndefined(rawShiftNotes),
        isRecurring,
        recurrencePattern: shiftRecurrenceRule?.toString(),
        recurrenceEndDate: shiftRecurEndDate,
        teamRoleAssignmentId: record.teamRoleAssignmentId,
      },
    });

    await performModifyShift(modifyShift, shiftModifiers, notify.success);

    onFinish();
  };

  const onRemoveSingleShift = () => {
    // Nothing should happen for new shift, this also make sure shift is not undefined.
    if (isNewShift) {
      return;
    }

    // Use original shift id if it is a recurrent shift.
    const deleteShiftId = providedShift.originalShiftId ?? providedShift.id;

    const shiftModifiers: ShiftModifiers = {
      ...emptyShiftModifiers,
      shiftsToDelete: [
        {
          shift_id: deleteShiftId,
          event: eventData('shift_deleted'),
        },
      ],
      notesToCreate: [
        createNoteToCreate(
          rootId(providedShift),
          shiftDeletedNote(new Date(providedShift.startTime))
        ),
      ],
    };

    void modal.confirm({
      title: providedShift.isRecurring
        ? `Remove this and all future shifts for ${userName} as ${record.roleName}`
        : `Remove shift for ${userName} as ${record.roleName} on ${format(
            record.selectedDate,
            shiftDateFormat
          )}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await performModifyShift(modifyShift, shiftModifiers, notify.success);
        onFinish();
      },
    });
  };

  // Note: this function will create a new recurrent shift after save.
  // Which means the new recurrent shift will not have original recurrent shift's exception date.
  const onSaveFutureRecurrentShift = () => {
    // Nothing should happen when this is a new shift or the shift is not derived shift.
    if (
      isNewShift ||
      !providedShift.isRecurring ||
      providedShift.originalShiftId === undefined
    ) {
      return;
    }

    const shiftModifiers: ShiftModifiers = futureRecurrentShiftModifiers({
      currentShift: providedShift,
      initialRecurShiftStartDate: firstShiftRecurRule.options.dtstart,
      startTime,
      endTime,
      shiftStatus,
      shiftNote: validNotesOrUndefined(rawShiftNotes),
      isRecurring,
      shiftRecurrenceRule,
      shiftRecurEndDate,
      record,
      selectedUsers,
    });

    const shiftModifiersWithChangelog: ShiftModifiers = addChangelogs(
      providedShift,
      selectedUsers.at(0),
      shiftModifiers
    );

    void modal.confirm({
      title: `Save this and all future shifts for ${userName} as ${record.roleName}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Save',
      okType: 'danger',
      cancelText: 'Cancel',
      okCancel: true,
      onOk: async () => {
        await performModifyShift(
          modifyShift,
          shiftModifiersWithChangelog,
          notify.success
        );
        onFinish();
      },
    });
  };

  const onRemoveFutureRecurrentShift = () => {
    // Nothing should happen when this is a new shift or the shift is not derived shift.
    if (
      isNewShift ||
      !providedShift.isRecurring ||
      providedShift.originalShiftId === undefined
    ) {
      return;
    }

    const shiftModifiers: ShiftModifiers = removeFutureRecurrentShiftModifiers(
      providedShift,
      firstShiftRecurRule.options.dtstart,
      validNotesOrUndefined(rawShiftNotes)
    );

    const shiftModifiersWithChangelog: ShiftModifiers = addChangelogs(
      providedShift,
      selectedUsers.at(0),
      shiftModifiers
    );

    void modal.confirm({
      title: `Remove this and all future shifts for ${userName} as ${record.roleName}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await performModifyShift(
          modifyShift,
          shiftModifiersWithChangelog,
          notify.success
        );
        onFinish();
      },
    });
  };

  const onRemoveSingleRecurringShift = () => {
    // Nothing should happen for new shift, this also make sure shift is not undefined.
    if (isNewShift) {
      return;
    }

    const shiftModifiers: ShiftModifiers = removeSingleRecurringShiftModifiers({
      shiftRecurrenceRule,
      isDerived: providedShift.isDerived,
      recurringProps: {
        freq: shiftRecurFreq,
        interval: shiftRecurInterval,
        end: shiftRecurEndDate,
        shiftStartTime: providedShift.startTime,
        shiftEndTime: providedShift.endTime,
      },
      shiftProps: {
        rootId: rootId(providedShift),
        id: providedShift.isDerived
          ? providedShift.originalShiftId
          : providedShift.id,
        startTime,
        endTime,
        status: shiftStatus,
        note: validNotesOrUndefined(rawShiftNotes),
        isRecurring,
        recurrencePattern: shiftRecurrenceRule?.toString(),
        recurrenceEndDate: shiftRecurEndDate,
        teamRoleAssignmentId: record.teamRoleAssignmentId,
      },
      shiftUserRoleAssignmentId: record.userRoleAssignmentId,
    });

    const shiftModifiersWithChangelog: ShiftModifiers = addChangelogs(
      providedShift,
      undefined,
      shiftModifiers
    );

    void modal.confirm({
      title: `Remove this shift for ${userName} as ${
        record.roleName
      } on ${format(record.selectedDate, shiftDateFormat)}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await performModifyShift(
          modifyShift,
          shiftModifiersWithChangelog,
          notify.success
        );
        onFinish();
      },
    });
  };

  const onSaveSingleDerivedShift = () => {
    // Nothing should happen for new shift, this also make sure shift is not undefined.
    if (isNewShift) {
      return;
    }

    const selectedSingleUser =
      selectedUsers.length === 1 ? selectedUsers[0] : undefined;

    const shiftToCreateEventAssignment = {
      user_role_assignment_id:
        selectedSingleUser === undefined
          ? record.userRoleAssignmentId
          : undefined,
      team_role_assignment_id: record.teamRoleAssignmentId,
      user_id: selectedSingleUser?.userId,
    };

    const shiftExceptionUpdateEvent: ShiftExceptionUpdate = {
      recurring_shift_id: providedShift.originalShiftId,
      exception_date: providedShift.startTime,
      start_time: startTime,
      end_time: endTime,
      shift_status: shiftStatus,
      note: toNoteContent(
        validNotesOrUndefined(rawShiftNotes) ?? 'Shift Exception Created'
      ),
      event: eventData('derived_shift_updated'),
      ...shiftToCreateEventAssignment,
    };

    const shiftModifiers: ShiftModifiers = {
      ...emptyShiftModifiers,
      shiftExceptionUpdates: [shiftExceptionUpdateEvent],
    };

    const shiftModifiersWithChangelog: ShiftModifiers = addChangelogs(
      providedShift,
      selectedSingleUser,
      shiftModifiers
    );

    void modal.confirm({
      title: `Save as separate shift (will be removed from recurrent pattern) for ${userName} as ${
        record.roleName
      } on ${format(record.selectedDate, shiftDateTimeFormat)}`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Save',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        await performModifyShift(
          modifyShift,
          shiftModifiersWithChangelog,
          notify.success
        );
        onFinish();
      },
    });
  };

  const onRecurrenceFreqChange = (e: RadioChangeEvent) => {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    const nextFreq = e.target.value as ShiftFreqOption;

    // Init the recurrent weekday if it is empty.
    if (
      nextFreq === shiftFreqOption.Values.WEEKLY &&
      shiftRecurWeekday.length === 0
    ) {
      // Get weekday of current shift start date.
      const startTimeWeekday = mapToRRuleWeekdayNumber(startTime);

      if (startTimeWeekday !== -1) {
        setShiftRecurWeekday([startTimeWeekday]);
      }
    }

    // Init the custom recurrent day if it is empty.
    if (
      nextFreq === shiftFreqOption.Values.CUSTOM &&
      shiftCustomRecurDay.length === 0
    ) {
      setShiftCustomRecurDay([0]);
    }

    setRecurFreqOption(nextFreq);
    setShiftRecurFreq(FreqOptionMap[nextFreq]);
  };

  const onRecurrenceByWeekdayChange = (targetWeekday: number) => {
    const initialStartTimeWeekday = mapToRRuleWeekdayNumber(initialStartTime);

    if (
      initialStartTimeWeekday === -1 ||
      initialStartTime === undefined ||
      shiftRecurFreq !== RRule.WEEKLY
    ) {
      return;
    }

    const nextShiftRecurWeekday = (): readonly number[] => {
      if (shiftRecurWeekday.includes(targetWeekday)) {
        const tempShiftRecurWeekday: readonly number[] =
          shiftRecurWeekday.filter((weekday) => weekday !== targetWeekday);

        // If the recurrence days are now empty, reset it to the initial weekday
        // selection, otherwise, sort the list
        return tempShiftRecurWeekday.length === 0
          ? [initialStartTimeWeekday]
          : tempShiftRecurWeekday.toSorted((n1, n2) => n1 - n2);
      } else {
        return [...shiftRecurWeekday, targetWeekday].sort((n1, n2) => n1 - n2);
      }
    };

    const nextShiftRecurWeekdayArray: readonly number[] =
      nextShiftRecurWeekday();

    // Update the state with the new selection
    setShiftRecurWeekday(nextShiftRecurWeekdayArray);

    // Determine the offset (if any) for the startTime without going backwards
    // in time
    const startTimeOffsetDays = (): number => {
      if (nextShiftRecurWeekdayArray.includes(initialStartTimeWeekday)) {
        return 0;
      }

      const daysPerWeek = 7;
      return nextShiftRecurWeekdayArray.reduce((lowestOffsetSoFar, curr) => {
        const maybeLowerOffset =
          curr > initialStartTimeWeekday
            ? curr - initialStartTimeWeekday
            : daysPerWeek - initialStartTimeWeekday + curr;

        return Math.min(maybeLowerOffset, lowestOffsetSoFar);
      }, 6);
    };

    const startTimeOffsetDaysToAdd = startTimeOffsetDays();

    // Update the start time to the earliest weekday in the selection that
    // is after the originally selected week day. We don't want to schedule
    // any shifts in the past.
    const nextStartTime = addDays(initialStartTime, startTimeOffsetDaysToAdd);
    setStartTime(nextStartTime);
    setEndTime(addHours(nextStartTime, shiftDurationInHours));
  };

  const onRecurrenceIntervalChange = (value: number | null) => {
    if (value !== null) {
      setShiftRecurInterval(value);
    }
  };

  const onRecurrencePeriodDayChange = (targetDay: number) => {
    if (shiftCustomRecurDay.includes(targetDay)) {
      const nextShiftRecurPeriodDay: readonly number[] =
        shiftCustomRecurDay.filter((day) => day !== targetDay);

      nextShiftRecurPeriodDay.length === 0
        ? setShiftCustomRecurDay([0])
        : setShiftCustomRecurDay(
            [...nextShiftRecurPeriodDay].sort((n1, n2) => n1 - n2)
          );
    } else {
      setShiftCustomRecurDay(
        [...shiftCustomRecurDay, targetDay].sort((n1, n2) => n1 - n2)
      );
    }
  };

  const onRecurrenceEndDateChange = (endDate: Date | null) => {
    setShiftRecurEndDate(endDate !== null ? endOfDay(endDate) : null);
  };

  const onIsRecurringChange = (e: CheckboxChangeEvent) => {
    setIsRecurring(e.target.checked);
  };

  return (
    <div style={{ display: 'block' }}>
      <ShiftDisplayHeading
        role={record.roleName}
        employee={userName}
        startTime={
          initialStartTime === undefined
            ? 'N/A'
            : format(startTime ?? initialStartTime, 'PPP')
        }
        isVacant={isVacantShift}
      />

      <Optional show={providedShift?.isDerived}>
        <div style={{ marginBottom: '10px' }}>
          <Alert type="info" message={<>This is a recurring shift.</>} />
        </div>
      </Optional>

      <SimpleTable
        firstColumnWidth={titleWidth}
        rows={[
          {
            title: (
              <label htmlFor="personnel" style={titleStyle}>
                Personnel
              </label>
            ),
            content: (
              <SearchUserSelect
                currentUserIds={record.userId}
                // We use multiple mode (i.e. not single mode) when it is a new shift so that we can assign
                // multiple users, but single mode afterwards - because then individual shift
                // buttons are created for each user initially assigned.
                singleMode={!isNewShift}
                formName="selectUserForm"
                fieldName="userIds"
                onChange={(selectedUsers) =>
                  setSelectedUsers(
                    selectedUsers.map(
                      (user): SimpleUser => ({
                        userId: user.id,
                        firstName: user.first_name,
                        lastName: user.last_name,
                        userName: user.user_name,
                      })
                    )
                  )
                }
              />
            ),
          },
          {
            title: (
              <label htmlFor="shift-status" style={titleStyle}>
                Status
              </label>
            ),
            content: (
              <Select
                id="shift-status"
                value={shiftStatus}
                onChange={setShiftStatus}
                style={{ width: '100%', maxWidth: '200px' }}
              >
                {Object.keys(shiftStatusMap)
                  .filter(
                    (status) =>
                      shiftStatusMap[status]?.isVacant === isVacantShift
                  )
                  .map((status) => (
                    <Option key={status} value={status} label={status}>
                      <ShiftStatusTag status={status} />
                    </Option>
                  ))}
              </Select>
            ),
          },
          {
            title: (
              <label htmlFor="shift-start-time" style={titleStyle}>
                Shift starts at
              </label>
            ),
            content: (
              <TimePicker
                id="shift-start-time"
                className="shift-date-picker"
                placeholder="Shift start time"
                value={startTime}
                format={shiftTimeFormat}
                onSelect={onStartTimeChange}
                allowClear={false}
                showNow={false}
                style={{ width: '100%', maxWidth: '200px' }}
                onChange={onStartTimeChange}
              />
            ),
          },
          {
            title: (
              <label htmlFor="duration" style={titleStyle}>
                Duration
              </label>
            ),
            content: (
              <InputDuration
                from={minimumShiftPeriodInHours}
                to={maximumShiftPeriodInHours}
                value={shiftDurationInHours}
                onChange={(v) => {
                  setShiftDurationInHours(v);
                  if (startTime !== undefined) {
                    setEndTime(addHours(startTime, v));
                  }
                }}
              />
            ),
          },
          {
            title: <label htmlFor="shift-end-time-text">Shift ends on</label>,
            content: (
              <Text className="shift-end-time-text">
                {startTime === undefined
                  ? undefined
                  : format(
                      addHours(startTime, shiftDurationInHours),
                      shiftDateTimeFormat
                    )}
              </Text>
            ),
            condition: endTime !== undefined && shiftDurationInHoursIsValid,
          },
          {
            content: <Divider style={{ marginTop: 10, marginBottom: 10 }} />,
          },
          {
            title: 'Is Recurring?',
            content: (
              <Checkbox
                id="is-recurring-shift-checkbox"
                checked={isRecurring}
                onChange={onIsRecurringChange}
              />
            ),
          },
        ]}
      />

      {/* Recurring pattern settings. */}
      <Optional show={isRecurring}>
        <SimpleTable
          firstColumnWidth={titleWidth}
          rows={[
            {
              title: (
                <label htmlFor="shift-recur-frequency" style={titleStyle}>
                  Frequency
                </label>
              ),
              content: (
                <Radio.Group
                  id="shift-recur-frequency"
                  buttonStyle="solid"
                  value={recurFreqOption}
                  onChange={onRecurrenceFreqChange}
                >
                  <Radio.Button value={shiftFreqOption.Values.DAILY}>
                    Daily
                  </Radio.Button>
                  <Radio.Button value={shiftFreqOption.Values.WEEKLY}>
                    Weekly
                  </Radio.Button>
                  <Radio.Button value={shiftFreqOption.Values.MONTHLY}>
                    Monthly
                  </Radio.Button>
                  <Radio.Button value={shiftFreqOption.Values.CUSTOM}>
                    Custom
                  </Radio.Button>
                </Radio.Group>
              ),
            },
            {
              title: <></>,
              content: (
                <div
                  id="recurrence-weekdays"
                  style={{
                    border: '1px solid lightgrey',
                    padding: '5px',
                    display: 'flex',
                    justifyContent: 'space-around',
                  }}
                >
                  {weekdays.map((weekday) => (
                    <CheckableTag
                      key={weekday.value}
                      checked={shiftRecurWeekday.includes(weekday.value)}
                      onChange={() =>
                        onRecurrenceByWeekdayChange(weekday.value)
                      }
                      style={{ margin: 0 }}
                    >
                      {weekday.text}
                    </CheckableTag>
                  ))}
                </div>
              ),
              condition: recurFreqOption === shiftFreqOption.Values.WEEKLY,
            },
            {
              title: (
                <label htmlFor="shift-recur-interval" style={titleStyle}>
                  Repeat ({FreqTextMap[recurFreqOption]})
                </label>
              ),
              content: (
                <InputNumber
                  id="shift-recur-interval"
                  min={1}
                  max={20}
                  style={{ width: '100%', maxWidth: '200px' }}
                  value={shiftRecurInterval}
                  onChange={onRecurrenceIntervalChange}
                />
              ),
            },
            {
              title: (
                <label htmlFor="shift-recur-custom-period" style={titleStyle}>
                  Repeat pattern
                </label>
              ),
              content: (
                <div
                  id="recurrence-pattern"
                  style={{
                    border: '1px solid lightgrey',
                    padding: '2px',
                    justifyContent: 'space-around',
                  }}
                >
                  {Array.from(Array(shiftRecurInterval).keys()).map((day) => (
                    <CheckableTag
                      key={day}
                      checked={shiftCustomRecurDay.includes(day)}
                      onChange={() => onRecurrencePeriodDayChange(day)}
                      style={{ margin: 1, width: '9%' }}
                    >
                      {day + 1}
                    </CheckableTag>
                  ))}
                </div>
              ),
              condition: recurFreqOption === shiftFreqOption.Values.CUSTOM,
            },
            {
              title: (
                <label htmlFor="shift-recur-end-time" style={titleStyle}>
                  Ends on
                </label>
              ),
              content: (
                <DatePicker
                  id="shift-recur-end-time"
                  defaultValue={initialStartTime}
                  value={shiftRecurEndDate}
                  onChange={onRecurrenceEndDateChange}
                  disabledDate={(currentDate) =>
                    startTime !== undefined && isBefore(currentDate, startTime)
                  }
                  style={{ width: '100%', maxWidth: '200px' }}
                />
              ),
            },
            {
              title: 'Shift pattern',
              content: (
                <b>
                  {removeApproximateFromRecurrentPatternText(
                    shiftRecurrenceRule
                      ?.rrules()
                      .map((rrule) => rrule.toText())
                      .join('\n')
                  )}
                </b>
              ),
              condition:
                shiftRecurrenceRule !== undefined &&
                recurFreqOption !== shiftFreqOption.Values.CUSTOM,
            },
          ]}
        />
      </Optional>

      {/* Notes */}
      <SimpleTable
        firstColumnWidth={titleWidth}
        rows={[
          {
            content: <Divider style={{ marginTop: 10, marginBottom: 10 }} />,
          },
          {
            title: (
              <label htmlFor="shift-notes">
                {isNewShift ? 'Notes' : 'Reason for Change'}
              </label>
            ),
            content: (
              <TextArea
                id="shift-notes"
                rows={2}
                value={validNotesOrUndefined(rawShiftNotes)}
                onChange={(e) => setShiftNotes(e.target.value)}
                maxLength={1000}
                showCount
              />
            ),
          },
        ]}
      />

      {/* Finish Buttons */}
      <div style={{ marginTop: '20px', textAlign: 'right', minHeight: '40px' }}>
        {/* Remove recurrent shift */}
        <Optional
          show={!isNewShift && editShiftPermission && providedShift.isRecurring}
        >
          <Optional show={isRecurring || providedShift?.isRecurring}>
            <Button
              className="remove-shift-future-btn"
              onClick={onRemoveFutureRecurrentShift}
              style={{
                float: 'left',
                marginRight: '10px',
                color: rfsTheme.darkRfsRed,
                borderColor: rfsTheme.darkRfsRed,
              }}
            >
              Remove future
            </Button>
          </Optional>
          <Button
            className="remove-shift-btn"
            onClick={onRemoveSingleRecurringShift}
            style={{
              float: 'left',
              color: rfsTheme.darkRfsRed,
              borderColor: rfsTheme.darkRfsRed,
            }}
          >
            Remove
          </Button>
        </Optional>

        {/* Remove non-recurrent shift */}
        <Optional
          show={
            !isNewShift && editShiftPermission && !providedShift.isRecurring
          }
        >
          <Button
            className="remove-single-shift-btn"
            onClick={onRemoveSingleShift}
            style={{
              float: 'left',
              color: rfsTheme.darkRfsRed,
              borderColor: rfsTheme.darkRfsRed,
            }}
          >
            Remove
          </Button>
        </Optional>

        {/* Create shift or update non-recurrent shift */}
        <Optional
          show={
            editShiftPermission &&
            (providedShift?.isDerived === undefined || !providedShift.isDerived)
          }
        >
          <Button
            className="save-shift-btn"
            type="primary"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={onSaveSingleShift}
          >
            Save
          </Button>
        </Optional>

        {/* Update recurrent shift */}
        <Optional
          show={!isNewShift && editShiftPermission && providedShift.isRecurring}
        >
          <Optional show={isRecurring || providedShift?.isRecurring}>
            <Button
              className="save-shift-future-btn"
              type="primary"
              onClick={onSaveFutureRecurrentShift}
              style={{
                marginRight: '10px',
              }}
            >
              Save future
            </Button>
          </Optional>
          <Button
            className="save-shift-btn"
            type="primary"
            onClick={onSaveSingleDerivedShift}
          >
            Save
          </Button>
        </Optional>
      </div>
    </div>
  );
};

export default ShiftManager;
